/**
 * Lingdong Post BBS Authentication System
 * Handles customer authentication and token management
 */

class LingdongPostBBS {
  constructor() {
    this.apiUrl = 'https://posts-admin.casabrews.com';
    this.tokenKey = 'ld_bbs_token';
    this.customerKey = 'ld_bbs_customer';
    this.token = null;
    this.customer = null;
    this.vueApps=[];

    // Initialize on page load
    this.init();
  }

  /**
   * Initialize the authentication system
   */
  init() {
    // Load stored token and customer data
    this.loadStoredData();
    
    // Check for customer changes and handle logout if customer is null
    this.checkCustomerChange();
    
    // Auto-login if Shopify customer exists and no valid token
    this.autoLoginCheck().then(() => {
      // if user is authenticated, try to redirect to return url
      if (this.isAuthenticated()) {
        this.tryToRedirectToReturnUrl();
      }
    });
  }

  /**
   * Load stored token and customer data from localStorage
   */
  loadStoredData() {
    try {
      const storedToken = localStorage.getItem(this.tokenKey);
      const storedCustomer = localStorage.getItem(this.customerKey);

      if (storedToken && storedCustomer) {
        this.token = storedToken;
        this.customer = JSON.parse(storedCustomer);

        // Validate token is not expired
        if (this.isTokenValid()) {
          console.log('Valid token found, user is authenticated');
          this.onAuthenticationSuccess();
        } else {
          console.log('Token expired, clearing stored data');
          this.clearStoredData();
        }
      }
    } catch (error) {
      console.error('Error loading stored authentication data:', error);
      this.clearStoredData();
    }
  }

  /**
   * Check if current token is valid (not expired)
   */
  isTokenValid() {
    if (!this.token) return false;

    try {
      // Parse JWT token payload
      const tokenParts = this.token.split('.');
      if (tokenParts.length !== 3) return false;

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token is expired
      return payload.exp && payload.exp > currentTime;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  /**
   * Clear stored authentication data
   */
  clearStoredData() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.customerKey);
    this.token = null;
    this.customer = null;
  }

  /**
   * Store authentication data in localStorage
   */
  storeAuthData(token, customer) {
    try {
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.customerKey, JSON.stringify(customer));
      this.token = token;
      this.customer = customer;
    } catch (error) {
      console.error('Error storing authentication data:', error);
    }
  }

  /**
   * Get Shopify customer data from theme
   */
  getShopifyCustomer() {
    // Check if Shopify customer object exists
    if (typeof window.ShopifyCustomer !== 'undefined' && window.ShopifyCustomer) {
      return window.ShopifyCustomer;
    }

    // Fallback: try to get from global customer object
    if (typeof customer !== 'undefined' && customer) {
      return customer;
    }

    return null;
  }

  /**
   * Check if auto-login is needed and perform it
   */
  async autoLoginCheck() {
    // Skip if already authenticated
    if (this.isAuthenticated()) {
      return;
    }

    const shopifyCustomer = this.getShopifyCustomer();

    if (shopifyCustomer && shopifyCustomer.id) {
      console.log('Shopify customer found, attempting auto-login');
      await this.loginWithShopifyData(shopifyCustomer);
    } else {
      // No customer logged in, ensure we're logged out
      console.log('No Shopify customer found, ensuring logout state');
      if (this.isAuthenticated()) {
        this.logout();
      }
    }
  }

  /**
   * Login with Shopify customer data
   */
  async loginWithShopifyData(shopifyCustomer) {
    try {
      const loginData = {
        email: shopifyCustomer.email,
        shopify_customer_id: shopifyCustomer.id,
        first_name: shopifyCustomer.first_name || '',
        last_name: shopifyCustomer.last_name || '',
        name: this.getCustomerName(shopifyCustomer)
      };

      const response = await this.performLogin(loginData);
      console.log('Auto-login response:', response);

      if (response.success) {
        console.log('Auto-login successful');
        this.storeAuthData(response.data.token, response.data.customer);
        this.onAuthenticationSuccess();
      } else {
        console.error('Auto-login failed:', response.message);
      }
    } catch (error) {
      console.error('Auto-login error:', error);
    }
  }

  /**
   * Get customer display name
   */
  getCustomerName(customer) {
    if (customer.name) return customer.name;

    const firstName = customer.first_name || '';
    const lastName = customer.last_name || '';

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return customer.email?.split('@')[0] || 'User';
  }

  /**
   * Perform login API call
   */
  async performLogin(loginData) {
    try {
      const response = await fetch(`${this.apiUrl}/api/v1/shopify/customer/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Login failed');
      }

      return result;
    } catch (error) {
      console.error('Login API error:', error);
      throw error;
    }
  }

  /**
   * Manual login method (for programmatic use)
   */
  async login(email, shopifyCustomerId, firstName = '', lastName = '', name = '') {
    const loginData = {
      email,
      shopify_customer_id: shopifyCustomerId,
      first_name: firstName,
      last_name: lastName,
      name: name || this.getCustomerName({ first_name: firstName, last_name: lastName, email })
    };

    try {
      const response = await this.performLogin(loginData);

      if (response.success) {
        this.storeAuthData(response.data.token, response.data.customer);
        this.onAuthenticationSuccess();
        return response;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Manual login error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  logout() {
    this.clearStoredData();
    this.onLogout();
    console.log('User logged out successfully');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.token && this.customer && this.isTokenValid();
  }

  /**
   * Get current user data
   */
  getCurrentUser() {
    return this.isAuthenticated() ? this.customer : null;
  }

  /**
   * Get authentication token
   */
  getToken() {
    return this.isAuthenticated() ? this.token : null;
  }

  /**
   * Get authentication headers for API calls
   */
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (this.getToken()) {
      headers['Authorization'] = `Bearer ${this.getToken()}`;
    }

    return headers;
  }

  /**
   * Make authenticated API call
   */
  async authenticatedFetch(url, options = {}) {
    const headers = {
      ...this.getAuthHeaders(),
      ...options.headers
    };

    return fetch(url, {
      ...options,
      headers
    });
  }

  /**
   * Event handlers
   */
  onAuthenticationSuccess() {
    // Dispatch custom event for other components to listen
    window.dispatchEvent(new CustomEvent('ldBBSAuthSuccess', {
      detail: {
        customer: this.customer,
        token: this.token
      }
    }));

    // Update UI elements that depend on authentication
    this.updateAuthenticatedUI();
  }

  onLogout() {
    // Dispatch custom event for logout
    window.dispatchEvent(new CustomEvent('ldBBSLogout'));

    // Update UI elements for logged out state
    this.updateUnauthenticatedUI();
  }

  /**
   * Update UI for authenticated state
   */
  updateAuthenticatedUI() {
    // Show authenticated elements
    document.querySelectorAll('[data-ld-auth="authenticated"]').forEach(el => {
      el.style.display = '';
    });

    // Hide unauthenticated elements
    document.querySelectorAll('[data-ld-auth="unauthenticated"]').forEach(el => {
      el.style.display = 'none';
    });

    // Update user info displays
    document.querySelectorAll('[data-ld-user="name"]').forEach(el => {
      el.textContent = this.customer?.name || '';
    });

    document.querySelectorAll('[data-ld-user="email"]').forEach(el => {
      el.textContent = this.customer?.email || '';
    });
  }

  /**
   * Update UI for unauthenticated state
   */
  updateUnauthenticatedUI() {
    // Hide authenticated elements
    document.querySelectorAll('[data-ld-auth="authenticated"]').forEach(el => {
      el.style.display = 'none';
    });

    // Show unauthenticated elements
    document.querySelectorAll('[data-ld-auth="unauthenticated"]').forEach(el => {
      el.style.display = '';
    });
  }

  /**
   * Get login URL with return URL parameter
   */
  getLoginUrl(returnUrl = null) {
    const loginBaseUrl = '/account/login';
    if (returnUrl == null) {
      return loginBaseUrl;
    }
    const currentUrl = returnUrl || window.location.href;

    // Create URL with return_url parameter (Shopify standard)
    const loginUrl = new URL(loginBaseUrl, window.location.origin);
    loginUrl.searchParams.set('return_url', currentUrl);
    localStorage.setItem('login_return_url', currentUrl);

    return loginUrl.toString();
  }

  /**
   * Redirect to login page with return URL
   */
  redirectToLogin(returnUrl = null) {
    const loginUrl = this.getLoginUrl(returnUrl);
    console.log('Redirecting to login with return URL:', loginUrl);
    window.location.href = loginUrl;
  }

  /**
   * Get return URL from localStorage
   */
  tryToRedirectToReturnUrl() {
    const returnUrl = localStorage.getItem('login_return_url');
    localStorage.removeItem('login_return_url');
    if (returnUrl) {
      window.location.href = returnUrl;
    }
  }

  /**
   * Show authentication required message with better UX
   */
  showAuthRequiredMessage(customMessage = null, customReturnUrl = null) {
    // Create a more user-friendly login prompt
    const message = customMessage ||
      'You need to be logged in to interact with posts. Would you like to login now?';

    this.showCustomModal({
      title: 'Login Required',
      message: message,
      confirmText: 'Login',
      cancelText: 'Cancel',
      returnUrl: customReturnUrl
    });
  }

  /**
   * Create and show a custom modal dialog using LingdongModal
   */
  showCustomModal(options = {}) {
    const {
      title = 'Login Required',
      message = 'You need to be logged in to perform this action.',
      confirmText = 'Login',
      cancelText = 'Cancel',
      returnUrl = null
    } = options;

    // Check if LingdongModal is available
    if (window.LingdongModal && typeof window.LingdongModal.open === 'function') {
      // Create a promise to handle the modal result
      return new Promise((resolve) => {
        window.LingdongModal.open({
          title: title,
          content: message,
          cancelText: cancelText,
          submitText: confirmText,
          onCancel: () => {
            resolve(false);
          },
          onSubmit: () => {
            this.redirectToLogin(returnUrl);
            resolve(true);
          }
        });
      });
    } else {
      // Fallback to browser confirm if LingdongModal is not available
      const result = confirm(message);
      if (result) {
        this.redirectToLogin(returnUrl);
      }
      return Promise.resolve(result);
    }
  }
  /**
   * Like a post
   */
  async likePost(postId) {
    if (!this.isAuthenticated()) {
      throw new Error('User must be authenticated to like posts');
    }

    try {
      const response = await this.authenticatedFetch(`${this.apiUrl}/api/v1/shopify/posts/like`, {
        method: 'POST',
        body: JSON.stringify({
          post_id: postId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to like post');
      }

      // Dispatch custom event for UI updates
      window.dispatchEvent(new CustomEvent('ldBBSPostLiked', {
        detail: { postId }
      }));

      console.log('Post liked successfully');
      return await response.json();
    } catch (error) {
      console.error('Error liking post:', error);
      throw error;
    }
  }

  /**
   * Unlike a post
   */
  async unlikePost(postId) {
    if (!this.isAuthenticated()) {
      throw new Error('User must be authenticated to unlike posts');
    }

    try {
      const response = await this.authenticatedFetch(`${this.apiUrl}/api/v1/shopify/posts/${postId}/unlike`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to unlike post');
      }

      // Dispatch custom event for UI updates
      window.dispatchEvent(new CustomEvent('ldBBSPostUnliked', {
        detail: { postId }
      }));

      console.log('Post unliked successfully');
      return await response.json();
    } catch (error) {
      console.error('Error unliking post:', error);
      throw error;
    }
  }

  /**
   * Toggle like status of a post
   */
  async togglePostLike(postId, currentLikeStatus = false) {
    try {
      if (currentLikeStatus) {
        return await this.unlikePost(postId);
      } else {
        return await this.likePost(postId);
      }
    } catch (error) {
      console.error('Error toggling post like:', error);
      throw error;
    }
  }

  /**
   * Create a new post
   *  Usage example:
  const postData = {
    content: "This is my amazing coffee review!",
    images: [
      {
        url: "https://example.com/image1.jpg",
        alt: "Coffee cup",
        width: 800,
        height: 600
      }
    ],
    products: [
      {
        shopify_product_id: 123456789,
        title: "Colombian Coffee Beans",
        featured_image: "https://cdn.shopify.com/product1.jpg",
        price: "$15.99",
        url: "https://www.myshop.com/products-best-conffee-machine-red",
        handle: "best-coffee-machine-red"
      }
    ]
  };

  window.LingdongBBS.createPost(postData);
   */
  async createPost(postData) {
    if (!this.isAuthenticated()) {
      throw new Error('User must be authenticated to create posts');
    }

    try {
      const response = await this.authenticatedFetch(`${this.apiUrl}/api/v1/shopify/posts`, {
        method: 'POST',
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create post');
      }

      const result = await response.json();

      // Dispatch custom event for UI updates
      window.dispatchEvent(new CustomEvent('ldBBSPostCreated', {
        detail: { post: result.data.post }
      }));

      console.log('Post created successfully');
      return result;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  /**
   * Edit/modify an existing post
   */
  async editPost(postId, postData) {
    if (!this.isAuthenticated()) {
      throw new Error('User must be authenticated to edit posts');
    }

    try {
      const response = await this.authenticatedFetch(`${this.apiUrl}/api/v1/shopify/posts/${postId}`, {
        method: 'PUT',
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to edit post');
      }

      const result = await response.json();

      // Dispatch custom event for UI updates
      window.dispatchEvent(new CustomEvent('ldBBSPostEdited', {
        detail: { postId, post: result.data.post }
      }));

      console.log('Post edited successfully');
      return result;
    } catch (error) {
      console.error('Error editing post:', error);
      throw error;
    }
  }

  /**
   * Check if Shopify customer has changed (logged out)
   */
  checkCustomerChange() {
    const currentShopifyCustomer = this.getShopifyCustomer();
    
    // If customer is null and we have stored data, logout
    if (!currentShopifyCustomer && this.isAuthenticated()) {
      console.log('Shopify customer logged out, clearing authentication');
      this.logout();
      return;
    }
    
    // If customer data changed, re-authenticate
    if (currentShopifyCustomer && this.customer) {
      const storedCustomerId = this.customer.shopify_customer_id || this.customer.id;
      const currentCustomerId = currentShopifyCustomer.id;
      
      if (storedCustomerId !== currentCustomerId) {
        console.log('Shopify customer changed, re-authenticating');
        this.clearStoredData();
        this.autoLoginCheck();
      }
    }
  }

  /**
   * Refresh token if needed
   */
  async refreshTokenIfNeeded() {
    if (!this.isTokenValid() && this.getShopifyCustomer()) {
      console.log('Token expired, refreshing...');
      this.autoLoginCheck();
    }
  }
  
  /**
   * Get a presigned URL for uploading an image to OSS
   * @param {string} filename - The name of the file to upload
   * @returns {Promise<Object>} - The presigned URL data
   */
  async getPresignedUrl(filename) {
    try {
      const apiBaseUrl = this.apiUrl;
      const presignedUrlEndpoint = `${apiBaseUrl}/api/v1/sts/presigned-url?filename=${encodeURIComponent(filename)}`;
      const authToken = this.getToken() || '';
      
      console.log('Fetching presigned URL from:', presignedUrlEndpoint);
      const response = await fetch(presignedUrlEndpoint, {
        method: 'GET',
        headers: { 
          'Authorization': authToken ? `Bearer ${authToken}` : '',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get presigned URL (${response.status}): ${errorText || response.statusText}`);
      }
      
      const presignedData = await response.json();
      if (!presignedData.data || !presignedData.data.url || !presignedData.data.key) {
        throw new Error('Invalid response format from presigned URL endpoint');
      }
      
      return presignedData.data;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw error;
    }
  }
  
  /**
   * Upload an image to OSS using a presigned URL
   * @param {File} file - The file object to upload
   * @param {Object} options - Upload options
   * @param {Function} options.onProgress - Progress callback function
   * @returns {Promise<Object>} - The uploaded image data
   */
  async uploadImageToOSS(file, options = {}) {
    if (!file) {
      throw new Error('No file provided for upload');
    }
    
    // Validate file type and size
    if (!['image/jpeg', 'image/png','image/jpg'].includes(file.type)) {
      throw new Error('Please select a JPG or PNG image only.');
    }
    
    if (file.size > 8 * 1024 * 1024) {
      throw new Error('Image size must be under 8MB. Please select a smaller image or compress this one.');
    }
    
    try {
      // Get presigned URL from API
      const presignedData = await this.getPresignedUrl(file.name);
      let { url: presignedUrl, key } = presignedData;
      
      console.log('Received presigned URL, uploading to OSS...');
      if (presignedUrl === null) {
        throw new Error('Failed to get presigned URL');
      }
      if(presignedUrl.startsWith('/')){
        presignedUrl = this.apiUrl + presignedUrl;
      }

      
      // Upload to OSS using PUT request
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': '' }
      });
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Failed to upload to OSS (${uploadResponse.status}): ${errorText || uploadResponse.statusText}`);
      }
      
      
      // Return the final image data
      const bucketDomain = `${this.apiUrl}${key}`;
      
      // Get actual image dimensions
      const imageDimensions = await this.getImageDimensions(file);
      console.log('Upload successful',bucketDomain,imageDimensions);
      
      return { 
        url: bucketDomain, 
        alt: file.name, 
        width: imageDimensions.width, 
        height: imageDimensions.height, 
        key: key
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Get image dimensions from a file object
   * @param {File} file - The image file
   * @returns {Promise<{width: number, height: number}>} - The image dimensions
   */
  getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Clean up object URL to prevent memory leaks
        URL.revokeObjectURL(img.src);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image for dimension calculation'));
      };
      img.src = URL.createObjectURL(file);
    });
  }
}

// Global instance
window.LingdongBBS = new LingdongPostBBS();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LingdongPostBBS;
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('Lingdong Post BBS initialized');
});